import { useMemo } from 'react'

interface ViewportWorldPosition {
  x: number
  y: number
}

interface ViewportSize {
  width: number
  height: number
}

/**
 * Calculate viewport offset for transform
 * viewportWorldPosition represents pan offset from origin (0, 0)
 * Screen formula: screen = -viewportWorldPosition * zoom
 * (negative because panning right moves viewport left, revealing content to the right)
 */
export function calculateViewportOffset(
  viewportWorldPosition: ViewportWorldPosition,
  zoomScale: number
): { x: number; y: number } {
  return {
    x: -viewportWorldPosition.x * zoomScale,
    y: -viewportWorldPosition.y * zoomScale
  }
}

/**
 * Calculate what world coordinate is under the fixed red dot at screen center
 * Screen center is at (50%, 50%) = (viewportWidth/2, viewportHeight/2)
 * Transform: screenPoint = worldPoint * zoom + offset
 * Inverse: worldPoint = (screenPoint - offset) / zoom
 */
export function useWorldUnderRedDot(
  viewportSize: ViewportSize,
  totalViewportOffset: { x: number; y: number },
  zoomScale: number
): { x: number; y: number } {
  return useMemo(() => {
    const screenCenterX = viewportSize.width / 2
    const screenCenterY = viewportSize.height / 2
    return {
      x: (screenCenterX - totalViewportOffset.x) / zoomScale,
      y: (screenCenterY - totalViewportOffset.y) / zoomScale
    }
  }, [viewportSize.width, viewportSize.height, totalViewportOffset.x, totalViewportOffset.y, zoomScale])
}

/**
 * Calculate effective display dimensions based on image and fallback dimensions
 */
export function getEffectiveDisplayDimensions(
  imageDimensions: { displayWidth: number; displayHeight: number } | null,
  fallbackWidth: number,
  fallbackHeight: number
): { width: number; height: number } {
  const effectiveDisplayWidth = imageDimensions?.displayWidth && imageDimensions.displayWidth > 0 
    ? imageDimensions.displayWidth 
    : fallbackWidth
  const effectiveDisplayHeight = imageDimensions?.displayHeight && imageDimensions.displayHeight > 0 
    ? imageDimensions.displayHeight 
    : fallbackHeight
  return { width: effectiveDisplayWidth, height: effectiveDisplayHeight }
}
